import { injectable, inject } from 'inversify';
import { UpdateCartItemQuantityUseCase } from '../../../domain/usecases/SaleUseCases';
import type { CartRepository, ProductRepository } from '../../../domain/repositories';
import { Cart } from '../../../domain/models';
import { TYPES } from '../../../di/types';

/**
 * Implementation of UpdateCartItemQuantityUseCase
 * This contains the business logic for updating cart item quantities
 */
@injectable()
export class UpdateCartItemQuantityUseCaseImpl implements UpdateCartItemQuantityUseCase {
  constructor(
    @inject(TYPES.CartRepository) private readonly cartRepository: CartRepository,
    @inject(TYPES.ProductRepository) private readonly productRepository: ProductRepository
  ) {}

  async execute(cartId: string, productId: string, quantity: number): Promise<Cart> {
    try {
      // Validate input parameters
      this.validateParameters(cartId, productId, quantity);

      // Get current cart
      const cart = await this.cartRepository.findById(cartId);
      if (!cart) {
        throw new Error('Cart not found');
      }

      // Find the item in the cart
      const itemIndex = cart.items.findIndex(item => item.productId === productId);
      if (itemIndex === -1) {
        throw new Error('Product not found in cart');
      }

      // Get product details for stock validation
      const product = await this.productRepository.findById(productId);
      if (!product) {
        throw new Error('Product not found');
      }

      // Validate stock availability
      this.validateStockAvailability(product, quantity);

      // Update the cart items
      const updatedItems = [...cart.items];
      const existingItem = updatedItems[itemIndex];

      if (quantity <= 0) {
        // Remove item if quantity is 0 or negative
        updatedItems.splice(itemIndex, 1);
      } else {
        // Update item quantity and recalculate subtotal
        updatedItems[itemIndex] = {
          ...existingItem,
          quantity,
          subtotal: this.calculateSubtotal(
            existingItem.unitPrice, 
            quantity, 
            existingItem.productDiscountPercentage
          )
        };
      }

      // Update cart with new items
      const updatedCart = await this.cartRepository.update(cartId, { items: updatedItems });

      console.log(`✅ Cart item quantity updated: ${productId} -> ${quantity}`);
      return updatedCart;
    } catch (error) {
      console.error('Update cart item quantity failed:', error);
      throw error;
    }
  }

  private validateParameters(cartId: string, productId: string, quantity: number): void {
    if (!cartId || cartId.trim().length === 0) {
      throw new Error('Cart ID is required');
    }

    if (!productId || productId.trim().length === 0) {
      throw new Error('Product ID is required');
    }

    if (quantity < 0) {
      throw new Error('Quantity cannot be negative');
    }

    if (quantity > 1000) {
      throw new Error('Quantity cannot exceed 1000 items');
    }
  }

  private validateStockAvailability(product: any, quantity: number): void {
    if (quantity > 0 && product.stock < quantity) {
      throw new Error(`Insufficient stock. Available: ${product.stock}, Requested: ${quantity}`);
    }
  }

  private calculateSubtotal(unitPrice: number, quantity: number, discountPercentage: number): number {
    const discountAmount = (unitPrice * discountPercentage) / 100;
    const discountedPrice = unitPrice - discountAmount;
    return discountedPrice * quantity;
  }
} 